import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, BarChart3, Settings, MessageCircle, LogOut, Medal, Award } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../common/Logo';

interface SlideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SlideMenu({ isOpen, onClose }: SlideMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, subscriber, signOut } = useAuth();

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/login');
  };

  const menuItems = [
    { icon: Home, label: 'Início', path: '/' },
    { icon: Medal, label: 'Rankings', path: '/rankings' },
    { icon: BarChart3, label: 'Estatísticas', path: '/stats' },
    { icon: Award, label: 'Torneios', path: '/tournaments' },
    { icon: Settings, label: 'Configurações', path: '/settings' },
    { icon: MessageCircle, label: 'Suporte', path: '/support' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 menu-overlay"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed left-0 top-0 bottom-0 z-50 w-72 bg-deep-blue border-r border-white/10"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <Logo size="sm" />
              <button
                onClick={onClose}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Info — doubles as the shortcut into Configurações */}
            <button
              onClick={() => handleNavigation('/settings')}
              className="w-full p-4 border-b border-white/10 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
                  {subscriber?.avatarUrl ? (
                    <img src={subscriber.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="w-full h-full bg-gradient-to-br from-turquoise to-coral flex items-center justify-center text-white font-bold text-lg">
                      {subscriber?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">
                    {subscriber?.name || 'Usuário'}
                  </p>
                  <p className="text-sm text-white/50 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </button>

            {/* Navigation */}
            <nav className="p-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNavigation(item.path)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors
                      ${isActive
                        ? 'bg-turquoise/20 text-turquoise'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Sign Out */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-coral hover:bg-coral/10 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sair</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
